<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSavedViews } from '@/composables/useSavedViews';
import { useMountedWhileOpen } from '@/composables/useMountedWhileOpen';

/**
 * Asks for a name and saves the list you are looking at as a new view: today's
 * "Save as new", reached from the Create menu and the section sidebar.
 */

const open = defineModel<boolean>('open', { required: true });

const { hasViewNameConflict, handlers } = useSavedViews();

// In the page only while open or fading out, so a closed dialog leaves no Teleport behind.
const { mounted: dialogMounted, onCloseFinished: onDialogClosed } = useMountedWhileOpen(open);

const name = ref('');
// Set once Save is pressed. The new view joins the list while the dialog is
// still fading out, and without this the closing dialog flashed "already
// exists" about the view it had just saved.
const saved = ref(false);
const nameError = computed(() => {
    const trimmed = name.value.trim();
    if (!saved.value && trimmed !== '' && hasViewNameConflict(trimmed)) {
        return `A saved view named "${trimmed}" already exists.`;
    }
    return '';
});
const canSave = computed(() => name.value.trim() !== '' && nameError.value === '');

watch(open, (isOpen) => {
    if (isOpen) {
        name.value = '';
        saved.value = false;
    }
});

function save() {
    if (!canSave.value) {
        return;
    }
    saved.value = true;
    handlers.onSaveNew(name.value.trim());
    open.value = false;
}
</script>

<template>
  <Dialog v-if="dialogMounted" v-model:open="open">
    <DialogContent class="sm:max-w-md" @close-auto-focus="onDialogClosed">
      <DialogHeader>
        <DialogTitle>New saved view</DialogTitle>
        <DialogDescription>Saves this list's filters and columns under a name.</DialogDescription>
      </DialogHeader>
      <form class="flex flex-col gap-2" @submit.prevent="save">
        <label for="new-saved-view-name" class="text-sm font-medium">Name</label>
        <Input id="new-saved-view-name" v-model="name" data-testid="new-saved-view-name" autocomplete="off" autofocus />
        <p v-if="nameError" class="text-sm text-destructive-foreground" role="alert">{{ nameError }}</p>
      </form>
      <DialogFooter>
        <Button variant="outline" @click="open = false">Cancel</Button>
        <Button :disabled="!canSave" data-testid="new-saved-view-save" @click="save">Save view</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
